import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import { SseProvider } from './context/SseContext';
import LoginPage from './pages/LoginPage';
import KanbanBoard from './pages/KanbanBoard';
import InvoiceDetail from './pages/InvoiceDetail';
import ApproverQueue from './pages/ApproverQueue';
import ClassificationMappings from './pages/ClassificationMappings';
import ProjectsAdmin from './pages/ProjectsAdmin';
import UsersAdmin from './pages/UsersAdmin';
import VendorsAdmin from './pages/VendorsAdmin';
import PayingBanksAdmin from './pages/PayingBanksAdmin';
import CompanyCodesAdmin from './pages/CompanyCodesAdmin';
import PaymentTracker from './pages/PaymentTracker';
import VatDashboard from './pages/VatDashboard';
import LuxembourgDashboard from './pages/LuxembourgDashboard';
import Reconciliation from './pages/Reconciliation';
import Budget from './pages/Budget';
import BudgetTutorial from './pages/BudgetTutorial';
import SettingsPage from './pages/SettingsPage';
import ApproveAll from './pages/ApproveAll';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: 16 }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            style={{
              padding: '8px 20px',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { currentUser } = useUser();
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Role-restricted route. The Settings page deals with routing and
// classification policy — that's the processor's (and admin's) territory,
// not the approver's. Anyone outside those roles gets bounced to the board.
const SETTINGS_ROLES = new Set(['processor', 'admin']);
function SettingsRoute({ children }) {
  const { currentUser } = useUser();
  if (!currentUser) return <Navigate to="/" replace />;
  if (!SETTINGS_ROLES.has(currentUser.role)) {
    return <Navigate to="/board" replace />;
  }
  return children;
}

// Users admin is admin-only: edits power approval-email routing.
function AdminRoute({ children }) {
  const { currentUser } = useUser();
  if (!currentUser) return <Navigate to="/" replace />;
  if (currentUser.role !== 'admin') return <Navigate to="/board" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <UserProvider>
        <SseProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route
              path="/tracker"
              element={
                <ProtectedRoute>
                  <PaymentTracker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/board"
              element={
                <ProtectedRoute>
                  <KanbanBoard />
                </ProtectedRoute>
              }
            />
            {/* Public (no ProtectedRoute): the email "Approve All" link must
                work without an app login — auth is the URL token. Placed
                before /approvals so the more specific path resolves first. */}
            <Route path="/approvals/approve-all" element={<ApproveAll />} />
            <Route
              path="/approvals"
              element={
                <ProtectedRoute>
                  <ApproverQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classifications"
              element={
                <ProtectedRoute>
                  <ClassificationMappings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <VendorsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/paying-banks"
              element={
                <ProtectedRoute>
                  <PayingBanksAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-codes"
              element={
                <ProtectedRoute>
                  <CompanyCodesAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vat"
              element={
                <ProtectedRoute>
                  <VatDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/luxembourg"
              element={
                <ProtectedRoute>
                  <LuxembourgDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reconciliation"
              element={
                <ProtectedRoute>
                  <Reconciliation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget"
              element={
                <ProtectedRoute>
                  <Budget view="dashboard" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget/forecast"
              element={
                <ProtectedRoute>
                  <Budget view="forecast" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget/review"
              element={
                <ProtectedRoute>
                  <Budget view="review" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget/slide"
              element={
                <ProtectedRoute>
                  <Budget view="slide" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget/actual-to-budget"
              element={
                <ProtectedRoute>
                  <Budget view="actualToBudget" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget/tutorial"
              element={
                <ProtectedRoute>
                  <BudgetTutorial />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <SettingsRoute>
                  <SettingsPage />
                </SettingsRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <UsersAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/invoice/:id"
              element={
                <ProtectedRoute>
                  <InvoiceDetail />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
        </SseProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;
